import { useState } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { App, DatePicker, Form, Input, InputNumber, Select, Switch } from 'antd'
import dayjs from 'dayjs'
import { z } from 'zod'
import {
  billCreateSchema,
  billUpdateSchema,
  type BillCreateInput,
  type billDetailSchema
} from '@super-mega/server/contracts/bill'
import { RANGE_PRESETS } from '@/const/range-presets.ts'
import { useTRPC } from '@/trpc/react.ts'
import { formatAmountInput } from '@/utils/money.ts'
import { toBillDraftFields, type BillFormValues } from './bill-form-values.ts'
import { BILL_KINDS } from './bill-search.ts'
import { FinanceSelect } from './finance-select.tsx'
import { PayeeQuickCreate } from './payee-quick-create.tsx'
import { refreshMoneyQueries } from './utils/refresh-money-queries.ts'

type Bill = z.infer<typeof billDetailSchema>

function getEditorTitle(isExpense: boolean, bill?: Bill) {
  if (isExpense && bill) {
    return '编辑支出账单'
  }
  if (isExpense) {
    return '新建支出账单'
  }
  if (bill) {
    return '编辑账单草稿'
  }
  return '新建账单草稿'
}

function getNewBillValues(source: BillCreateInput['source'], payable: boolean) {
  let feeType: BillFormValues['feeType']
  let description: string
  if (payable) {
    feeType = 'other'
    description = ''
  } else if (source?.type === 'rental') {
    feeType = 'rent'
    description = '租金'
  } else {
    feeType = 'service'
    description = '服务费'
  }
  return { feeType, description, originalAmount: null }
}

function getSaveErrorMessage(error: unknown) {
  if (error instanceof z.ZodError) {
    return error.issues[0]?.message ?? '保存失败'
  }
  if (error instanceof Error) {
    return error.message
  }
  return '保存失败'
}

function getBillFieldRestrictions(bill: Bill | undefined, isExpense: boolean) {
  return {
    hasChargeRule: Boolean(bill?.rentalChargeRuleId || bill?.serviceChargeRuleId),
    hidesQuantityPricing: isExpense || Boolean(bill?.serviceChargeRuleId)
  }
}

export function BillEditor({
  source,
  bill: billProp,
  payable = false,
  onClose,
  onSaved
}: {
  source: BillCreateInput['source']
  payable?: boolean
  bill?: Bill
  onClose: () => void
  onSaved: (id: number) => void
}) {
  const isExpense = payable || billProp?.direction === 'payable'
  const [form] = Form.useForm<
    BillFormValues & {
      siteId?: number
      organizationId?: number
      payeeId?: number
      expenseBusinessId?: number
      paymentDetails?: string
      expenseUrgent?: boolean
    }
  >()
  const organizationId = Form.useWatch<number | undefined>('organizationId', form)
  const [bill] = useState(billProp)
  const { hasChargeRule, hidesQuantityPricing } = getBillFieldRestrictions(bill, isExpense)
  const [idempotencyKey] = useState(() => crypto.randomUUID())
  const [open, setOpen] = useState(true)
  const [savedId, setSavedId] = useState<number>()
  const trpc = useTRPC()
  const cache = useQueryClient()
  const { message } = App.useApp()
  const create = useMutation(trpc.bill.createDraft.mutationOptions())
  const update = useMutation(trpc.bill.updateDraft.mutationOptions())
  return (
    <ModalForm<
      BillFormValues & {
        expenseBusinessId?: number
        paymentDetails?: string
        expenseUrgent?: boolean
        organizationId?: number
        payeeId?: number
        businessId?: number
        salespersonId?: number
        siteId?: number
      }
    >
      name="bill-editor"
      form={form}
      dateFormatter={false}
      open={open}
      onOpenChange={setOpen}
      title={getEditorTitle(isExpense, bill)}
      layout="vertical"
      modalProps={{
        afterClose: () => (savedId === undefined ? onClose() : onSaved(savedId)),
        destroyOnHidden: true,
        mask: { closable: false }
      }}
      submitter={{
        searchConfig: { submitText: '保存草稿' },
        submitButtonProps: { loading: create.isPending || update.isPending }
      }}
      initialValues={
        bill
          ? {
              period: bill.periodStart && bill.periodEnd ? [dayjs(bill.periodStart), dayjs(bill.periodEnd)] : undefined,
              dueAt: bill.dueAt ? dayjs(bill.dueAt) : undefined,
              expenseBusinessId: bill.expenseBusinessId,
              paymentDetails: bill.paymentDetails,
              expenseUrgent: bill.expenseUrgent,
              note: bill.note,
              feeType: bill.feeType,
              description: bill.description,
              originalAmount: bill.originalAmount,
              quantity: bill.quantity,
              unitPrice: bill.unitPrice
            }
          : getNewBillValues(source, payable)
      }

      onFinish={async (values) => {
        try {
          const fields = {
            ...toBillDraftFields(values),
            ...(isExpense
              ? {
                  expenseBusinessId: values.expenseBusinessId ?? null,
                  paymentDetails: values.paymentDetails ?? null,
                  expenseUrgent: values.expenseUrgent ?? false
                }
              : {})
          }
          const saved = bill
            ? await update.mutateAsync(
                billUpdateSchema.parse({ ...fields, billId: bill.id, expectedUpdatedAt: bill.updatedAt })
              )
            : await create.mutateAsync(
                billCreateSchema.parse({
                  ...fields,
                  source,
                  idempotencyKey,
                  ...(payable
                    ? {
                        direction: 'payable',
                        organizationId: values.organizationId,
                        payeeId: values.payeeId,
                        businessId: values.businessId,
                        salespersonId: values.salespersonId,
                        siteId: values.siteId
                      }
                    : {})
                })
              )
          await refreshMoneyQueries(cache, trpc)
          void message.success('账单草稿已保存')
          setSavedId(saved.id)
          setOpen(false)
          return true
        } catch (error) {
          void message.error(getSaveErrorMessage(error))
          return false
        }
      }}
    >
      {payable && !bill && (
        <div className="grid gap-x-4 sm:grid-cols-2">
          <Form.Item name="organizationId" label="付款主体" rules={[{ required: true }]}>
            <FinanceSelect
              purpose="bill.read"
              kind="organization"
              onChange={() => form.setFieldValue('siteId', undefined)}
            />
          </Form.Item>
          <Form.Item name="payeeId" label="付款对象" rules={[{ required: true }]}>
            <PayeeQuickCreate />
          </Form.Item>
          <Form.Item name="siteId" label="归属场地">
            <FinanceSelect purpose="bill.read" kind="site" organizationId={organizationId} disabled={!organizationId} />
          </Form.Item>
          <Form.Item name="salespersonId" label="归属业务员">
            <FinanceSelect purpose="bill.read" kind="member" />
          </Form.Item>
        </div>
      )}
      {isExpense && (
        <>
          <Form.Item name="expenseBusinessId" label="支出业务" rules={[{ required: true, message: '请选择支出业务' }]}>
            <FinanceSelect purpose="bill.read" kind="expenseBusiness" />
          </Form.Item>
          <Form.Item
            name="paymentDetails"
            label="银行转款信息"
            extra="填写收款户名、开户行、账号或微信 / 支付宝等付款信息。"
          >
            <Input.TextArea maxLength={5000} rows={3} />
          </Form.Item>
          <Form.Item name="expenseUrgent" label="加急付款" valuePropName="checked">
            <Switch />
          </Form.Item>
        </>
      )}
      <p className="text-muted-foreground mb-2">
        {source ? `${source.type === 'rental' ? '租赁' : '服务'}订单 #${source.orderId}` : '独立应付费用'} ·
        手动填写本次费用，不会创建周期收费规则；草稿确认后才计入应收应付。
      </p>
      <div className="grid gap-x-4 sm:grid-cols-2">
        <Form.Item name="period" label="费用期间">
          <DatePicker.RangePicker presets={RANGE_PRESETS} disabled={hasChargeRule} className="w-full!" />
        </Form.Item>
        <Form.Item name="dueAt" label={payable || bill?.direction === 'payable' ? '应付日期' : '应收日期'}>
          <DatePicker className="w-full!" />
        </Form.Item>
      </div>
      <div className="grid gap-x-3 sm:grid-cols-2">
        <Form.Item name="feeType" label="费用类型" hidden={isExpense} rules={[{ required: true }]}>
          <Select
            disabled={hasChargeRule}
            options={Object.entries(BILL_KINDS).map(([value, label]) => ({ value, label }))}
          />
        </Form.Item>
        <Form.Item name="description" label="费用说明" rules={[{ required: true, whitespace: true }]}>
          <Input maxLength={255} />
        </Form.Item>
        <Form.Item name="originalAmount" label="金额（元）" extra="未确定时留空；确定免费时填写 0。">
          <InputNumber stringMode min="0" max="9999999999999999.99" precision={2} className="w-full!" />
        </Form.Item>
        <Form.Item name="quantity" label="数量" hidden={hidesQuantityPricing}>
          <InputNumber stringMode min="0" precision={0} className="w-full!" />
        </Form.Item>
        <Form.Item name="unitPrice" label="单价" hidden={hidesQuantityPricing}>
          <InputNumber stringMode min="0" precision={2} formatter={formatAmountInput} className="w-full!" />
        </Form.Item>
      </div>
      <Form.Item className="mt-4" name="note" label="备注">
        <Input.TextArea rows={2} maxLength={5000} />
      </Form.Item>
    </ModalForm>
  )
}
